/* eslint-disable camelcase */
import { useQuery } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';
import storage from '@pray/shared/utils/storage';

import type {
  ConversationInsightsData,
  ConversationPlatform,
  GetConversationInsightsParams,
  TopicData,
  TrendingTopic,
  SentimentData,
  PrayerIntentData,
  TopicTimelineEntry,
  Emotion,
  PrayerIntent,
} from './types';

// API response types (snake_case from backend)
type ApiTopicData = {
  topic: string;
  count: number;
  avg_sentiment: number;
  trend?: 'up' | 'down' | 'stable';
  trend_percent?: number;
};

type ApiWordCloudData = {
  topics: ApiTopicData[];
  total_messages: number;
};

type ApiTrendingTopic = {
  topic: string;
  percent_change: number;
  message: string;
  direction: 'up' | 'down';
};

type ApiSentimentData = {
  avg_score: number;
  dominant_emotion: Emotion;
  distribution: Record<string, number>;
  trend: 'improving' | 'declining' | 'stable';
  message_count: number;
};

type ApiPrayerIntentData = {
  intent: PrayerIntent;
  count: number;
  percentage: number;
};

type ApiTopicTimelineEntry = {
  date: string;
  topics: Record<string, number>;
};

type ApiConversationInsightsData = {
  word_cloud: ApiWordCloudData;
  topic_timeline: ApiTopicTimelineEntry[];
  trending: ApiTrendingTopic[];
  sentiment: ApiSentimentData;
  prayer_intents: ApiPrayerIntentData[];
};

type ApiConversationInsightsResponse = {
  data: ApiConversationInsightsData;
};

// Transform snake_case API response to camelCase for frontend
function transformInsightsResponse(apiData: ApiConversationInsightsData): ConversationInsightsData {
  return {
    wordCloud: {
      topics: apiData.word_cloud.topics.map(
        (t): TopicData => ({
          topic: t.topic,
          count: t.count,
          avgSentiment: t.avg_sentiment,
          trend: t.trend,
          trendPercent: t.trend_percent,
        })
      ),
      totalMessages: apiData.word_cloud.total_messages,
    },
    topicTimeline: apiData.topic_timeline.map(
      (entry): TopicTimelineEntry => ({
        date: entry.date,
        topics: entry.topics,
      })
    ),
    trending: apiData.trending.map(
      (t): TrendingTopic => ({
        topic: t.topic,
        percentChange: t.percent_change,
        message: t.message,
        direction: t.direction,
      })
    ),
    sentiment: {
      avgScore: apiData.sentiment.avg_score,
      dominantEmotion: apiData.sentiment.dominant_emotion,
      distribution: apiData.sentiment.distribution,
      trend: apiData.sentiment.trend,
      messageCount: apiData.sentiment.message_count,
    } as SentimentData,
    prayerIntents: apiData.prayer_intents.map(
      (p): PrayerIntentData => ({
        intent: p.intent,
        count: p.count,
        percentage: p.percentage,
      })
    ),
  };
}

export async function getConversationInsights(
  params: GetConversationInsightsParams
): Promise<ConversationInsightsData> {
  const { artistId, dateStart, dateEnd, platform } = params;

  if (!artistId) throw Error('Artist ID is required');
  if (!dateStart) throw Error('Start date is required');
  if (!dateEnd) throw Error('End date is required');

  const queryParams: Record<string, string> = {
    start_date: dateStart,
    end_date: dateEnd,
  };

  if (platform && platform.length > 0) {
    queryParams.platform = platform.join(',');
  }

  const url = `/web/studio/artists/${artistId}/conversations/analytics/insights`;
  const response = await api.executeGetRequest<ApiConversationInsightsResponse>(url, queryParams);

  return transformInsightsResponse(response.data.data);
}

export function useConversationInsights(
  artistId: string,
  dateStart: string,
  dateEnd: string,
  platform?: ConversationPlatform[]
) {
  const user = storage.getUser();

  return useQuery({
    enabled: !!artistId && !!user?.is_registered && !!dateStart && !!dateEnd,
    queryKey: queryKeys.artistConversationInsights(artistId, { dateStart, dateEnd, platform }),
    queryFn: () =>
      getConversationInsights({
        artistId,
        dateStart,
        dateEnd,
        platform,
      }),
    staleTime: 15_000, // Consider fresh for 15 seconds
    refetchInterval: 30_000, // Auto-refresh every 30 seconds for real-time feel
    refetchIntervalInBackground: false, // Only when tab is active
  });
}
